/**
 * @file
 * Real-estate exposed-form controls: sort swap and results title placement.
 *
 * @description
 * The list/map switching itself lives in the shared tp:view-tabs component; this
 * file only enhances the controls row that the real-estate exposed form renders
 * next to those tabs.
 */

((Drupal, once) => {
  'use strict';

  const REAL_ESTATE_RESULTS_TITLE_SELECTOR = '[data-real-estate-results-title]';
  const REAL_ESTATE_RESULTS_TITLE_SOURCE_SELECTOR = '[data-real-estate-results-title-source]';
  const REAL_ESTATE_SORT_SWAP_SELECTOR = '[data-real-estate-sort-swap]';
  const REAL_ESTATE_SORT_OPTION_SELECTOR = '.form-type-radio';
  const REAL_ESTATE_SORT_LABEL_SELECTOR = '.form-element-label';
  const ENHANCED_SORT_SWAP_CLASS = 'tp-real-estate-sort-radios--enhanced';
  const TARGET_SORT_OPTION_CLASS = 'tp-real-estate-sort-radios__option--target';
  const RESULTS_TITLE_AMOUNT_CLASS = 'tp-real-estate-view-controls__results-title-amount';

  /**
   * Marks the unchecked sort option as the only visible swap target.
   */
  function refreshSortSwap(sortSwap: HTMLElement): void {
    const radios = Array.from(sortSwap.querySelectorAll<HTMLInputElement>('input[type="radio"]'));
    const targetRadio =
      radios.find((radio) => !radio.checked && !radio.disabled) ?? radios.find((radio) => !radio.disabled);

    radios.forEach((radio) => {
      const option = radio.closest<HTMLElement>(REAL_ESTATE_SORT_OPTION_SELECTOR);
      const isTarget = radio === targetRadio;
      option?.classList.toggle(TARGET_SORT_OPTION_CLASS, isTarget);

      const label = option?.querySelector<HTMLElement>(REAL_ESTATE_SORT_LABEL_SELECTOR);
      const labelText = label?.textContent?.trim();
      if (labelText) {
        radio.setAttribute('aria-label', labelText);
      }
    });

    sortSwap.classList.add(ENHANCED_SORT_SWAP_CLASS);
  }

  /**
   * Enhances sort radios to show only the option the user can switch to.
   */
  function setupSortSwaps(context: Document | HTMLElement): void {
    once('tp-real-estate-sort-swap', REAL_ESTATE_SORT_SWAP_SELECTOR, context).forEach((element) => {
      const sortSwap = element as HTMLElement;
      refreshSortSwap(sortSwap);

      sortSwap.addEventListener('change', () => {
        refreshSortSwap(sortSwap);
      });
    });
  }

  /**
   * Returns the paragraph root that contains the real-estate list and map panels.
   */
  function getRealEstateRoot(element: HTMLElement): HTMLElement {
    return element.closest<HTMLElement>('.paragraph--tp-real-estate-search') ?? document.body;
  }

  /**
   * Wraps the first visible word in the moved results title.
   */
  function highlightResultsTitleAmount(placeholder: HTMLElement): void {
    const textWalker = document.createTreeWalker(placeholder, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        return node.textContent?.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });
    const textNode = textWalker.nextNode();
    if (!(textNode instanceof Text) || textNode.parentElement?.closest(`.${RESULTS_TITLE_AMOUNT_CLASS}`)) {
      return;
    }

    const text = textNode.textContent ?? '';
    const match = text.match(/^(\s*)(\S+)([\s\S]*)$/);
    if (!match) {
      return;
    }

    const [, leadingWhitespace, firstWord, remainingText] = match;
    const fragment = document.createDocumentFragment();
    if (leadingWhitespace) {
      fragment.append(document.createTextNode(leadingWhitespace));
    }

    const amount = document.createElement('span');
    amount.className = RESULTS_TITLE_AMOUNT_CLASS;
    amount.textContent = firstWord;
    fragment.append(amount);

    if (remainingText) {
      fragment.append(document.createTextNode(remainingText));
    }

    textNode.replaceWith(fragment);
  }

  /**
   * Moves the rendered Views header into the controls row placeholder.
   *
   * @description
   * Views renders the result count/title after the exposed form. This keeps that
   * server-rendered content as the source of truth, then moves its child nodes
   * into the controls placeholder after initial render and after Views AJAX.
   */
  function syncRealEstateResultsTitles(): void {
    document.querySelectorAll<HTMLElement>(REAL_ESTATE_RESULTS_TITLE_SELECTOR).forEach((placeholder) => {
      const root = getRealEstateRoot(placeholder);
      const source = root.querySelector<HTMLElement>(REAL_ESTATE_RESULTS_TITLE_SOURCE_SELECTOR);
      if (!source) {
        return;
      }

      placeholder.replaceChildren(...Array.from(source.childNodes));
      highlightResultsTitleAmount(placeholder);
      placeholder.hidden = placeholder.childNodes.length === 0;
      source.remove();
    });
  }

  /**
   * Sort and results-title enhancements for the real-estate search results.
   */
  Drupal.behaviors.tpRealEstateViewControls = {
    attach(context: Document | HTMLElement) {
      setupSortSwaps(context);
      syncRealEstateResultsTitles();
    },
  };
})(Drupal, once);
